/**
 * Bloco de perguntas de QUALIFICAÇÃO (padrão de captura FEO).
 *
 * Fonte única usada por:
 *  - `QualifierForm` (aulas experimentais, funis, etc.)
 *  - Quizzes (`/quizz-dor-pos-todas`, `/quizz-pos-todas`), onde este bloco
 *    roda ANTES das perguntas do quiz e é o único que alimenta CRM/RD.
 */

export type QualifierOption = {
  label: string;
  pontos: number;
  dealBreaker?: boolean;
};

export type QualifierQuestion = {
  id: string;
  pergunta: string;
  options: QualifierOption[];
};

export type QualifierBranch = {
  id: "estudante" | "fisioterapeuta" | "fisio_pos";
  questions: QualifierQuestion[];
};

export type QualifierAnswer = {
  pergunta: string;
  resposta: string;
  pontos: number;
  dealBreaker: boolean;
};

export const BRANCHES: Record<string, QualifierBranch> = {
  estudante: {
    id: "estudante",
    questions: [
      {
        id: "periodo",
        pergunta: "Em qual período você está?",
        options: [
          { label: "1º ao 3º ano", pontos: 0, dealBreaker: true },
          { label: "4º ano", pontos: 0, dealBreaker: true },
          { label: "5º ano", pontos: 2 },
          { label: "Me formo nos próximos 6 meses", pontos: 2 },
        ],
      },
      {
        id: "quando_inicia",
        pergunta: "Quando você pretende iniciar sua primeira pós-graduação?",
        options: [
          { label: "Imediatamente após me formar", pontos: 2 },
          { label: "Nos primeiros 6 meses após me formar", pontos: 2 },
          { label: "Entre 6 e 12 meses após me formar", pontos: 1 },
          { label: "Pretendo ganhar experiência antes de fazer pós", pontos: 0, dealBreaker: true },
          { label: "Não quero fazer pós", pontos: 0, dealBreaker: true },
        ],
      },
      {
        id: "etapa_escolha",
        pergunta: "Em qual etapa você está na escolha da sua pós?",
        options: [
          { label: "Já sei a área, mas ainda não sei a instituição", pontos: 2 },
          { label: "Estou comparando opções no mercado", pontos: 2 },
          { label: "Quero me formar antes de começar a buscar", pontos: 0, dealBreaker: true },
          { label: "Ainda não pensei sobre isso", pontos: 0, dealBreaker: true },
        ],
      },
    ],
  },
  fisioterapeuta: {
    id: "fisioterapeuta",
    questions: [
      {
        id: "pretende_iniciar",
        pergunta: "Você pretende iniciar uma pós-graduação nos próximos meses?",
        options: [
          { label: "Sim, nos próximos 3 meses", pontos: 2 },
          { label: "Sim, entre 3 e 6 meses", pontos: 2 },
          { label: "Sim, entre 6 e 12 meses", pontos: 1 },
          { label: "Ainda estou avaliando", pontos: 1 },
          { label: "Não pretendo iniciar uma pós", pontos: 0, dealBreaker: true },
        ],
      },
      {
        id: "ja_pesquisou",
        pergunta: "Você já pesquisou alguma pós-graduação anteriormente?",
        options: [
          { label: "Sim, estou pesquisando neste momento", pontos: 2 },
          { label: "Sim, já pesquisei anteriormente", pontos: 2 },
          { label: "Não, ainda não comecei a olhar pra isso", pontos: 0, dealBreaker: true },
        ],
      },
      {
        id: "o_que_falta",
        pergunta: "O que falta para você iniciar uma pós-graduação?",
        options: [
          { label: "Encontrar o conteúdo programático certo para meus objetivos", pontos: 2 },
          { label: "Confiar na instituição da pós que gostei do conteúdo", pontos: 2 },
          { label: "Encontrar um preço que cabe no meu bolso", pontos: 1 },
          { label: "Organizar melhor meu tempo e rotina", pontos: 1 },
          { label: "Ainda não decidi se este é o momento ideal", pontos: 0, dealBreaker: true },
        ],
      },
    ],
  },
  fisio_pos: {
    id: "fisio_pos",
    questions: [
      {
        id: "area_pos_atual",
        pergunta: "Você já possui pós-graduação em qual área?",
        options: [
          { label: "Orto e Trauma", pontos: 1 },
          { label: "Esporte", pontos: 1 },
          { label: "Dor", pontos: 1 },
          { label: "Gerontologia", pontos: 1 },
          { label: "Outra área da fisioterapia", pontos: 1 },
        ],
      },
      {
        id: "gatilho_nova_pos",
        pergunta: "O que mais pesaria na sua decisão de iniciar uma nova pós?",
        options: [
          { label: "Estudar uma nova área complementar à minha atuação", pontos: 2 },
          { label: "Me atualizar cientificamente", pontos: 2 },
          { label: "Continuar crescendo na minha carreira", pontos: 2 },
          { label: "Uma condição especial de matrícula", pontos: 2 },
          { label: "O reconhecimento da instituição", pontos: 2 },
          { label: "Não tenho interesse em uma nova pós", pontos: 0, dealBreaker: true },
        ],
      },
    ],
  },
};

export const ELEGIBILIDADE: QualifierQuestion = {
  id: "situacao",
  pergunta: "Qual é a sua situação profissional?",
  options: [
    { label: "Sou estudante de fisioterapia", pontos: 1 },
    { label: "Sou fisioterapeuta", pontos: 2 },
    { label: "Sou fisioterapeuta com pós-graduação", pontos: 2 },
    { label: "Outro profissional da saúde", pontos: 0, dealBreaker: true },
  ],
};

export function getBranchKey(situacao: string): keyof typeof BRANCHES | null {
  if (situacao.startsWith("Sou estudante")) return "estudante";
  if (situacao === "Sou fisioterapeuta") return "fisioterapeuta";
  if (situacao.startsWith("Sou fisioterapeuta com pós")) return "fisio_pos";
  return null;
}

/**
 * Classificação padrão frio/morno/quente.
 * Estudantes de graduação são sempre MORNO (regra do projeto).
 */
export function classifyRespostas(arr: QualifierAnswer[]) {
  const total = arr.reduce((s, r) => s + r.pontos, 0);
  const max = arr.length * 2;
  const pct = max > 0 ? Math.round((total / max) * 100) : 0;
  const hasDealBreaker = arr.some((r) => r.dealBreaker);
  const isEstudante = arr.some(
    (r) => r.pergunta.toLowerCase().includes("situação") && r.resposta.startsWith("Sou estudante"),
  );
  let classificacao: "quente" | "morno" | "frio" = "frio";
  if (isEstudante) classificacao = hasDealBreaker ? "frio" : "morno";
  else if (hasDealBreaker) classificacao = "frio";
  else if (pct >= 75) classificacao = "quente";
  else if (pct >= 50) classificacao = "morno";
  return { total, pct, classificacao, hasDealBreaker };
}

/**
 * Sequência do bloco de qualificação: elegibilidade + perguntas do branch.
 * Retorna a próxima pergunta não respondida (ou null quando o bloco terminou).
 */
export function nextQualifierQuestion(answers: QualifierAnswer[]): QualifierQuestion | null {
  if (answers.length === 0) return ELEGIBILIDADE;
  const situacao = answers[0]?.resposta ?? "";
  const branchKey = getBranchKey(situacao);
  const branch = branchKey ? BRANCHES[branchKey] : null;
  if (!branch) return null; // "Outro profissional da saúde" → bloco encerra
  const idx = answers.length - 1;
  return branch.questions[idx] ?? null;
}

/** Estimativa do total de perguntas do bloco de qualificação (para a barra). */
export function estimateQualifierTotal(answers: QualifierAnswer[]): number {
  const situacao = answers[0]?.resposta ?? "";
  const branchKey = situacao ? getBranchKey(situacao) : null;
  const branch = branchKey ? BRANCHES[branchKey] : null;
  if (!situacao) return 4; // estimativa antes da 1ª resposta
  return 1 + (branch ? branch.questions.length : 0);
}
